import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { FileText, Download, Loader2, AlertCircle, Eye, Calendar, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SharedDocument() {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    loadSharedDocument();
  }, [shareId]);

  const loadSharedDocument = async () => {
    try {
      const response = await axios.get(`${API}/shared/${shareId}`);
      setData(response.data);
    } catch (err) {
      console.error("Failed to load shared document:", err);
      setError(err.response?.data?.detail || "Document not found or link expired");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const getDocTypeLabel = (type) => {
    const labels = {
      invoice: "Invoice",
      receipt: "Receipt",
      quote: "Quote",
      estimate: "Estimate",
      "expense-claim": "Expense Claim",
      statement: "Statement"
    };
    return labels[type] || type;
  };

  const downloadPDF = async () => {
    if (!previewRef.current) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${docType}-${docNumber}.pdf`);
      
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading document...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Document Not Available</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/">
            <Button>Go to LedgerAMT</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { document, share_info: shareInfo } = data || {};
  const docType = document?.meta?.doc_type || "document";
  const docNumber = document?.meta?.doc_number || "";
  const status = document?.meta?.status || "draft";
  const currency = document?.meta?.currency || "USD";

  return (
    <div className="min-h-screen bg-gray-100" data-testid="shared-document-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold">{getDocTypeLabel(docType)} {docNumber}</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <Eye className="w-3 h-3" />
                {shareInfo?.view_count} views
                {shareInfo?.expires_at && (
                  <>
                    <span>•</span>
                    <Calendar className="w-3 h-3" />
                    Expires {formatDate(shareInfo.expires_at)}
                  </>
                )}
              </p>
            </div>
          </div>
          
          <Button onClick={downloadPDF} disabled={downloading} variant="outline">
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>
      </div>

      {/* Document Preview */}
      <div className="max-w-4xl mx-auto p-4 sm:p-8">
        <div 
          ref={previewRef}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
          style={{ minHeight: "800px" }}
        >
          <div className="p-8 sm:p-12">
            {/* Header */}
            <div className="flex justify-between items-start mb-8">
              <div>
                {document?.issuer?.logo_url ? (
                  <img 
                    src={document.issuer.logo_url} 
                    alt="Logo" 
                    className="h-20 object-contain mb-4"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Building2 className="w-10 h-10 text-primary" />
                  </div>
                )}
                <h2 className="text-xl font-bold">{document?.issuer?.name || "Business Name"}</h2>
                <p className="text-sm text-gray-500">{document?.issuer?.email}</p>
                <p className="text-sm text-gray-500">{document?.issuer?.phone}</p>
              </div>
              <div className="text-right">
                <h1 className="text-3xl font-bold text-primary uppercase">{getDocTypeLabel(docType)}</h1>
                <p className="text-lg font-mono mt-2">{docNumber}</p>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${
                  status === "paid" ? "bg-emerald-100 text-emerald-800" :
                  status === "issued" ? "bg-blue-100 text-blue-800" :
                  status === "overdue" ? "bg-red-100 text-red-800" :
                  "bg-gray-100 text-gray-800"
                }`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </div>
              </div>
            </div>

            {/* Dates and Client */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Bill To</h3>
                <p className="font-medium">{document?.recipient?.name || document?.client_snapshot?.name || "-"}</p>
                <p className="text-sm text-gray-600">{document?.recipient?.email || document?.client_snapshot?.email}</p>
                <p className="text-sm text-gray-600">{document?.recipient?.address || document?.client_snapshot?.address}</p>
              </div>
              <div className="text-right">
                <div className="mb-2">
                  <span className="text-sm text-gray-500">Issue Date: </span>
                  <span className="font-medium">{formatDate(document?.meta?.issue_date)}</span>
                </div>
                {document?.meta?.due_date && (
                  <div>
                    <span className="text-sm text-gray-500">Due Date: </span>
                    <span className="font-medium">{formatDate(document?.meta?.due_date)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Items Table */}
            <table className="w-full mb-8">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 text-sm font-semibold text-gray-600">Description</th>
                  <th className="text-right py-3 text-sm font-semibold text-gray-600 w-20">Qty</th>
                  <th className="text-right py-3 text-sm font-semibold text-gray-600 w-28">Price</th>
                  <th className="text-right py-3 text-sm font-semibold text-gray-600 w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {document?.content?.items?.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3">{item.description}</td>
                    <td className="py-3 text-right">{item.quantity}</td>
                    <td className="py-3 text-right">{formatCurrency(item.unit_price, currency)}</td>
                    <td className="py-3 text-right font-medium">{formatCurrency(item.amount, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64">
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatCurrency(document?.totals?.subtotal, currency)}</span>
                </div>
                {document?.totals?.tax > 0 && (
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Tax ({document?.totals?.tax_rate || 0}%)</span>
                    <span>{formatCurrency(document?.totals?.tax, currency)}</span>
                  </div>
                )}
                {document?.totals?.discount > 0 && (
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Discount</span>
                    <span className="text-red-600">-{formatCurrency(document?.totals?.discount, currency)}</span>
                  </div>
                )}
                <div className="flex justify-between py-3 border-t-2 border-gray-200 font-bold text-lg">
                  <span>Total</span>
                  <span className="text-primary">{formatCurrency(document?.totals?.total, currency)}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {document?.content?.notes && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Notes</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{document?.content?.notes}</p>
              </div>
            )}

            {/* Terms */}
            {document?.content?.terms && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Terms & Conditions</h3>
                <p className="text-gray-600 text-sm whitespace-pre-wrap">{document?.content?.terms}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Powered by <Link to="/" className="text-primary hover:underline">LedgerAMT</Link></p>
        </div>
      </div>
    </div>
  );
}
